import { Heart, Moon, Activity, Flame, Footprints, Zap, ArrowRight, Gauge } from "lucide-react";

const ROWS = [
  {
    Icon: Activity, color: "#22D3EE",
    title: "HRV (Heart Rate Variability)",
    weight: "Recovery 35%",
    body: "Overnight median in milliseconds. ArmIQ compares your last 7 days vs the prior 7 to detect parasympathetic drag — a drop of 15%+ is a yellow flag, 30%+ pulls you off live throwing.",
  },
  {
    Icon: Heart, color: "#EF4444",
    title: "Resting Heart Rate",
    weight: "Recovery 25%",
    body: "Morning RHR baseline. A sustained elevation of +5 bpm vs your personal baseline (computed from the prior 14 days) drops your recovery score and nudges you toward a B/C/D hybrid.",
  },
  {
    Icon: Moon, color: "#A78BFA",
    title: "Sleep Duration + REM",
    weight: "Recovery 20%",
    body: "Total hours and REM time. Two or more sub-6h nights in a row triggers a 'sleep debt' modifier that compounds with HRV/RHR. REM is tracked separately for cognitive readiness.",
  },
  {
    Icon: Heart, color: "#F472B6",
    title: "In-game Heart Rate (max + live)",
    weight: "Hot-Pitcher tile",
    body: "Granular HR samples stream during practice + bullpens. The Hot-Pitcher tile lights up when your bpm sustains 90+ over your RHR for 3+ minutes — that's how coaches see fatigue in real time.",
  },
  {
    Icon: Flame, color: "#F59E0B",
    title: "VO₂ Max",
    weight: "Mechanics input",
    body: "Aerobic capacity baseline. ArmIQ uses this to size your conditioning prescription and to spot losses in fitness after a layoff — small drops here predict velocity decay 2–3 weeks out.",
  },
  {
    Icon: Footprints, color: "#34D399",
    title: "Steps + Active Energy",
    weight: "Load 15%",
    body: "Total daily volume + active kcal feed your acute:chronic load ratio alongside throw count. A high step day + a heavy bullpen ≠ recovered — ArmIQ rolls them together.",
  },
  {
    Icon: Zap, color: "#F87171",
    title: "Workouts (auto-detected)",
    weight: "Load 20%",
    body: "Each Apple Watch workout (lifting, running, etc.) is logged with type + duration + kcal. ArmIQ treats it as additional acute load on top of throwing — so a heavy lift the day before a start is no longer invisible.",
  },
];

export default function HealthExplainer() {
  return (
    <div className="rounded-xl ink-card-elevated p-6" data-testid="health-explainer">
      <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
        How it works · The pipeline
      </div>
      <h2 className="font-display font-bold text-xl text-white mt-1">
        How your watch data combines into one Readiness Score
      </h2>
      <p className="text-sm text-white/60 mt-2">
        Seven different signals stream from your Apple Watch / Wear OS device through this webhook and get weighted into a single 0–100 number that drives today's prescribed hybrid (A / B / C / D).
      </p>

      <div className="mt-5 space-y-2.5">
        {ROWS.map(({ Icon, color, title, weight, body }) => (
          <div key={title} className="rounded-lg p-3 flex items-start gap-3"
               style={{ backgroundColor: "var(--ink-1)", border: "1px solid var(--ink-border)" }}>
            <span className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${color}22`, color }}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-white">{title}</div>
                <span className="text-[9px] tracking-[0.18em] font-extrabold px-1.5 py-0.5 rounded uppercase"
                      style={{ color, borderWidth: 1, borderColor: color, borderStyle: "solid" }}>
                  {weight}
                </span>
              </div>
              <div className="text-xs text-white/60 mt-1">{body}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-lg p-4 flex items-center gap-3 flex-wrap" style={{ backgroundColor: "var(--ink-1)", border: "1px dashed var(--ink-border)" }}>
        <Gauge className="h-6 w-6 shrink-0" style={{ color: "var(--brand-secondary)" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.18em] uppercase text-white/40">The combined output</div>
          <div className="text-sm text-white/80 mt-0.5 flex items-center gap-2 flex-wrap">
            All seven signals + your daily self-log <ArrowRight className="h-3 w-3 text-white/40" />
            <span className="font-display font-bold text-white">0–100 Readiness Score</span>
            <ArrowRight className="h-3 w-3 text-white/40" />
            <span className="font-mono-num font-bold" style={{ color: "var(--brand-secondary)" }}>Hybrid A / B / C / D</span>
            <ArrowRight className="h-3 w-3 text-white/40" />
            <span className="text-white/70">Today's prescribed routine.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
